import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import type { CalendarEvent, MeetingRoom, DirectoryUser } from "@/types/calendar";
import { BOOKING_CONFIG } from "@/config/rooms";
import { useUsers } from "@/hooks/useUsers";
import { cn } from "@/lib/utils";

interface BookingModalProps {
  rooms: MeetingRoom[];
  defaultRoomId?: string | null;
  defaultStart?: Date;
  defaultEnd?: Date;
  onClose: () => void;
  onBooked: (roomId: string, event: CalendarEvent) => void;
}

function roundUpToSlot(d: Date, slotMin: number): Date {
  const ms = slotMin * 60 * 1000;
  return new Date(Math.ceil(d.getTime() / ms) * ms);
}

function formatLocalDateTimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatKoreanTime(d: Date): string {
  return d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function BookingModal({ rooms, defaultRoomId, defaultStart, defaultEnd, onClose, onBooked }: BookingModalProps) {
  const { users, loading: usersLoading, error: usersError } = useUsers();

  const initialStart = useMemo(
    () => defaultStart ?? roundUpToSlot(new Date(), BOOKING_CONFIG.slotRoundingMin),
    [defaultStart],
  );

  const initialDurationMin = useMemo(() => {
    if (defaultStart && defaultEnd) {
      return Math.round((defaultEnd.getTime() - defaultStart.getTime()) / 60000);
    }
    return BOOKING_CONFIG.defaultDurationMin;
  }, [defaultStart, defaultEnd]);

  const [roomId, setRoomId] = useState<string>(defaultRoomId || rooms[0]?.id || "");
  const [title, setTitle] = useState("");
  const [startStr, setStartStr] = useState(formatLocalDateTimeInput(initialStart));
  const [durationMin, setDurationMin] = useState<number>(initialDurationMin);
  const [organizerQuery, setOrganizerQuery] = useState("");
  const [organizer, setOrganizer] = useState<DirectoryUser | null>(null);
  const [organizerFocused, setOrganizerFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const organizerInputRef = useRef<HTMLInputElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // 과거 시간 선택 방지: 모달 오픈 시점의 현재시각을 시작 하한으로
  const minStartStr = useMemo(() => formatLocalDateTimeInput(new Date()), []);

  const startDate = useMemo(() => new Date(startStr), [startStr]);
  const endDate = useMemo(
    () => new Date(startDate.getTime() + durationMin * 60 * 1000),
    [startDate, durationMin],
  );

  const filteredUsers = useMemo(() => {
    const q = organizerQuery.trim().toLowerCase();
    if (!q) return users.slice(0, 30);
    return users
      .filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.fullName || "").toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [organizerQuery, users]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  // Portal dropdown positioning — avoids triggering modal-body scroll
  useEffect(() => {
    if (!organizerFocused || organizer) {
      setDropdownPos(null);
      return;
    }
    const update = () => {
      const rect = organizerInputRef.current?.getBoundingClientRect();
      if (rect) {
        setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [organizerFocused, organizer, organizerQuery]);

  const canSubmit =
    !!roomId &&
    !!organizer &&
    !!title.trim() &&
    !isNaN(startDate.getTime()) &&
    endDate > startDate &&
    startDate.getTime() > Date.now() - 60 * 1000 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !organizer) return;
    setSubmitting(true);
    setServerError(null);
    setWarning(null);
    try {
      const res = await fetch("/api/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId,
          organizerEmail: organizer.email,
          title: title.trim(),
          startISO: startDate.toISOString(),
          endISO: endDate.toISOString(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setServerError(data.error || `요청 실패 (${res.status})`);
        return;
      }
      if (data.roomDeclined) {
        setWarning(data.message || "회의실이 사용 중입니다.");
        return;
      }
      if (data.roomId && data.event) {
        const event: CalendarEvent = {
          ...data.event,
          start: new Date(data.event.start),
          end: new Date(data.event.end),
        };
        onBooked(data.roomId, event);
      }
      onClose();
    } catch (e) {
      setServerError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRoom = rooms.find((r) => r.id === roomId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        className={cn(
          "relative w-full md:w-auto md:min-w-[440px] md:max-w-lg",
          "max-h-[90vh] flex flex-col",
          "bg-background rounded-t-2xl md:rounded-xl shadow-xl",
          "animate-in fade-in slide-in-from-bottom-4 md:slide-in-from-bottom-0 md:zoom-in-95 duration-200",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-2 rounded-t-2xl md:rounded-t-xl"
          style={{ backgroundColor: selectedRoom?.color || "#888" }}
        />

        <div className="flex justify-center pt-2 md:hidden">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 w-8 h-8 flex items-center justify-center rounded-full bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="p-5 md:p-6 overflow-y-auto flex-1 min-h-0 space-y-4">
          <h2 className="text-lg md:text-xl font-semibold">즉시 예약</h2>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">회의실</label>
            <div className="flex flex-wrap gap-1.5">
              {rooms.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRoomId(r.id)}
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium border transition-all",
                    roomId === r.id
                      ? "text-white border-transparent shadow-sm"
                      : "bg-background text-foreground border-border hover:border-foreground/30",
                  )}
                  style={roomId === r.id ? { backgroundColor: r.color } : undefined}
                >
                  {r.displayName}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">제목</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 주간 기획 회의"
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">예약자 (organizer)</label>
            <div className="relative">
              <input
                ref={organizerInputRef}
                type="text"
                value={organizer ? `${organizer.fullName} · ${organizer.email}` : organizerQuery}
                onChange={(e) => {
                  setOrganizer(null);
                  setOrganizerQuery(e.target.value);
                }}
                onFocus={() => setOrganizerFocused(true)}
                onBlur={() => setTimeout(() => setOrganizerFocused(false), 150)}
                placeholder={usersLoading ? "사용자 불러오는 중..." : "이름 또는 이메일로 검색"}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
                autoComplete="off"
              />
            </div>
            {organizerFocused && !organizer && dropdownPos && createPortal(
              <div
                className="fixed z-[60] bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
                style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
              >
                {usersError && <div className="px-3 py-2 text-xs text-destructive">{usersError}</div>}
                {!usersError && filteredUsers.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">일치하는 사용자 없음</div>
                )}
                {filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setOrganizer(u);
                      setOrganizerQuery("");
                      setOrganizerFocused(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted text-sm"
                  >
                    {u.photoUrl ? (
                      <img src={u.photoUrl} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                        {(u.fullName || u.email)[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.fullName}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    </div>
                  </button>
                ))}
              </div>,
              document.body,
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">시작</label>
            <input
              type="datetime-local"
              value={startStr}
              min={minStartStr}
              onChange={(e) => setStartStr(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">시간</label>
            <div className="flex flex-wrap gap-1.5">
              {BOOKING_CONFIG.durationPresets.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDurationMin(m)}
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium border transition-all",
                    durationMin === m
                      ? "bg-foreground text-background border-transparent"
                      : "bg-background text-foreground border-border hover:border-foreground/30",
                  )}
                >
                  {m < 60 ? `${m}분` : m % 60 === 0 ? `${m / 60}시간` : `${Math.floor(m / 60)}시간 ${m % 60}분`}
                </button>
              ))}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {formatKoreanTime(startDate)} — {formatKoreanTime(endDate)}
            </div>
          </div>

          {warning && (
            <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-800 dark:text-yellow-300">
              {warning}
            </div>
          )}
          {serverError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {serverError}
            </div>
          )}
        </div>

        <div className="p-4 md:p-5 border-t border-border flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium border border-border hover:bg-muted transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-semibold text-white transition-all",
              canSubmit ? "bg-primary hover:opacity-90" : "bg-muted-foreground/40 cursor-not-allowed",
            )}
          >
            {submitting ? "예약 중..." : "예약 확정"}
          </button>
        </div>
      </div>
    </div>
  );
}
